import { createFileRoute, Link, redirect, useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IntegrationProvider } from '@/api/admin-integrations-types'
import type {
	IntegrationConfig,
	IntegrationFieldDescriptor,
	IntegrationOverride,
	IntegrationProviderType
} from '@/api/admin-integrations-types'
import {
	useCreateIntegrationOverride,
	useDeleteIntegrationOverride,
	usePutIntegrationGlobalConfig,
	useRegisterRapydDevice,
	useUpdateIntegrationOverride
} from '@/api/api-mutations'
import {
	preloadGetIntegrationConfig,
	useGetIntegrationConfig,
	useGetMe,
	useListPointsOfSale,
	useListSalesChannels,
	usePollRapydDeviceToken,
	useListTeyaStores,
	useListTeyaTerminals
} from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { usePrompt } from '@/components/base/prompt'
import { Select } from '@/components/base/select'
import { Switch } from '@/components/base/switch'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { ArrowUturnLeft } from '@/icons/arrow-uturn-left'
import { Trash } from '@/icons/trash'
import { isAdminOrDeveloperRole } from '@/util/invite-role-label'

// Field keys whose value is a credential reused for later terminal requests — never render them in
// full in the dashboard (screenshots / shoulder-surfing). Shown masked so an admin can still tell a
// value is set and roughly which one.
const MASKED_FIELD_KEYS = new Set(['deviceToken'])

const maskCredential = (value: string): string => (value.length <= 4 ? '••••' : `••••${value.slice(-4)}`)

const isKnownProvider = (provider: string): provider is IntegrationProviderType =>
	(Object.values(IntegrationProvider) as string[]).includes(provider)

// Rapyd registers a serial for one hour; poll roughly once a second (Rapyd rate-limits to 1/sec per
// serial) and give up after a couple of minutes so an abandoned pairing stops refetching.
const RAPYD_POLL_INTERVAL_MS = 1500
const RAPYD_PAIR_TIMEOUT_MS = 2 * 60 * 1000
// Rapyd requires a PAX serial number of at least 10 characters.
const RAPYD_SERIAL_MIN_LENGTH = 10

export const Route = createFileRoute('/_authenticated/settings/integrations/$provider')({
	loader: async ({ context, params: { provider } }) => {
		// Reject unsupported slugs before they reach the API and surface as a raw error state.
		if (!isKnownProvider(provider)) {
			throw redirect({ to: '/settings/integrations' })
		}
		await preloadGetIntegrationConfig(context, { path: { provider } })
	},
	component: ProviderConfigPage
})

type FieldValues = Record<string, unknown>

type ProviderLabels = ReturnType<typeof useI18n>['L']['Settings']['Integrations']['Providers']
type FieldLabels = ReturnType<typeof useI18n>['L']['Settings']['Integrations']['Fields']

const providerLabel = (L: ReturnType<typeof useI18n>['L'], provider: string): string => {
	const providers = L.Settings.Integrations.Providers
	return provider in providers ? providers[provider as keyof ProviderLabels].Name() : provider
}

const fieldLabel = (L: ReturnType<typeof useI18n>['L'], key: string): string => {
	const fields = L.Settings.Integrations.Fields
	return key in fields ? fields[key as keyof FieldLabels]() : key
}

function ProviderConfigPage() {
	const { L } = useI18n()
	const { provider } = useParams({ from: '/_authenticated/settings/integrations/$provider' })
	const query = useGetIntegrationConfig({ provider: provider as IntegrationProviderType })
	const me = useGetMe()
	// USER gets a read-only view; every write control is hidden and inputs are disabled.
	const isAdmin = isAdminOrDeveloperRole(me.data?.role)

	return (
		<SingleColumnPage>
			<div className='space-y-6'>
				<div>
					<Link
						to='/settings/integrations'
						className='txt-compact-small mb-2 inline-flex items-center gap-x-1 text-ui-fg-subtle hover:text-ui-fg-base'
					>
						<ArrowUturnLeft />
						{L.Settings.Integrations.Config.Back()}
					</Link>
					<Heading level='h1'>{providerLabel(L, provider)}</Heading>
				</div>

				{query.isPending ? (
					<LoadingSpinner />
				) : query.data ? (
					<ConfigSections config={query.data} isAdmin={isAdmin} />
				) : (
					<p className='txt-compact-small text-ui-fg-subtle'>{L.Error.SomethingWentWrong()}</p>
				)}
			</div>
		</SingleColumnPage>
	)
}

function ConfigSections({ config, isAdmin }: { config: IntegrationConfig; isAdmin: boolean }) {
	return (
		<>
			<GlobalConfigCard config={config} isAdmin={isAdmin} />
			<OverridesCard config={config} isAdmin={isAdmin} />
		</>
	)
}

function GlobalConfigCard({ config, isAdmin }: { config: IntegrationConfig; isAdmin: boolean }) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const putGlobal = usePutIntegrationGlobalConfig()
	const [saving, setSaving] = useState(false)

	const [values, setValues] = useState<FieldValues>(() => initialValues(config.fields, config.global.values))

	const missingRequired = useMemo(
		() => missingRequiredGlobalFields(config.fields, values, config.global.secretsSet),
		[config.fields, values, config.global.secretsSet]
	)

	const save = useCallback(async () => {
		if (missingRequired) return
		setSaving(true)
		const result = await putGlobal({
			path: { provider: config.provider },
			body: { values: cleanValues(config.fields, values, config.global.secretsSet, false) }
		})
		setSaving(false)
		if (result.isSuccess) {
			toast.success(C.Saved())
		} else {
			toast.error(C.SaveError())
		}
	}, [putGlobal, config, values, C, missingRequired])

	return (
		<Container className='divide-y p-0'>
			<ContainerHeader
				heading={
					<div>
						<Heading level='h2'>{C.Global()}</Heading>
						<p className='txt-compact-small text-ui-fg-subtle'>{C.GlobalDescription()}</p>
					</div>
				}
			/>
			<div className='space-y-4 px-3 py-4 sm:px-6'>
				{config.fields.map(field => (
					<FieldInput
						key={field.key}
						field={field}
						value={values[field.key]}
						secretSet={config.global.secretsSet.includes(field.key)}
						onChange={next => setValues(prev => ({ ...prev, [field.key]: next }))}
						provider={config.provider}
						teyaStoreId={typeof values.storeId === 'string' ? values.storeId : undefined}
						apiKeyConfigured={config.global.secretsSet.includes('apiKey')}
						siblingValues={values}
						onPatchField={(key, val) => setValues(prev => ({ ...prev, [key]: val }))}
						disabled={!isAdmin}
					/>
				))}
				{isAdmin && (
					<div className='flex justify-end'>
						<Button size='small' onClick={() => void save()} isLoading={saving} disabled={missingRequired}>
							{C.Save()}
						</Button>
					</div>
				)}
			</div>
		</Container>
	)
}

function OverridesCard({ config, isAdmin }: { config: IntegrationConfig; isAdmin: boolean }) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const [addOpen, setAddOpen] = useState(false)
	const [editing, setEditing] = useState<IntegrationOverride | null>(null)

	const overridableFields = useMemo(() => config.fields.filter(f => f.overridable), [config.fields])
	const description =
		config.overrideScope === 'salesChannel'
			? C.OverridesDescriptionSalesChannel()
			: C.OverridesDescriptionPointOfSale()

	// Providers whose fields are all global (e.g. Struct) have nothing to override, so don't render an
	// empty override flow. The back end rejects override writes for these providers as well.
	if (overridableFields.length === 0) {
		return null
	}

	return (
		<Container className='divide-y p-0'>
			<ContainerHeader
				heading={
					<div>
						<Heading level='h2'>{C.Overrides()}</Heading>
						<p className='txt-compact-small text-ui-fg-subtle'>{description}</p>
					</div>
				}
				rightSide={
					isAdmin ? (
						<Button size='small' variant='secondary' onClick={() => setAddOpen(true)}>
							{C.AddOverride()}
						</Button>
					) : undefined
				}
			/>

			{config.overrides.length === 0 ? (
				<div className='txt-compact-small px-3 py-4 text-ui-fg-subtle sm:px-6'>{C.NoOverrides()}</div>
			) : (
				<ul className='divide-y'>
					{config.overrides.map(override => (
						<OverrideRow
							key={override.featureId}
							config={config}
							override={override}
							isAdmin={isAdmin}
							onEdit={() => setEditing(override)}
						/>
					))}
				</ul>
			)}

			{addOpen && (
				<AddOverrideDrawer
					config={config}
					overridableFields={overridableFields}
					onClose={() => setAddOpen(false)}
				/>
			)}
			{editing && (
				<EditOverrideDrawer
					config={config}
					override={editing}
					overridableFields={overridableFields}
					onClose={() => setEditing(null)}
				/>
			)}
		</Container>
	)
}

function OverrideRow({
	config,
	override,
	isAdmin,
	onEdit
}: {
	config: IntegrationConfig
	override: IntegrationOverride
	isAdmin: boolean
	onEdit: () => void
}) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const prompt = usePrompt()
	const deleteOverride = useDeleteIntegrationOverride()

	const summary = Object.entries(override.values ?? {})
		.map(
			([key, value]) =>
				`${fieldLabel(L, key)}: ${MASKED_FIELD_KEYS.has(key) ? maskCredential(String(value)) : String(value)}`
		)
		.join(' · ')

	const handleDelete = useCallback(async () => {
		const ok = await prompt({
			title: C.DeleteOverride(),
			description: C.DeleteOverrideConfirm(),
			confirmText: C.Delete(),
			cancelText: L.General.Cancel(),
			variant: 'danger'
		})
		if (!ok) return
		const result = await deleteOverride({
			path: { provider: config.provider, featureId: String(override.featureId) }
		})
		if (result.isSuccess) {
			toast.success(C.Saved())
		} else {
			toast.error(C.SaveError())
		}
	}, [prompt, C, L, deleteOverride, config.provider, override.featureId])

	return (
		<li className='flex items-center justify-between gap-3 px-3 py-3 sm:px-6'>
			<div className='min-w-0'>
				<div className='txt-compact-small-plus truncate'>{override.targetName}</div>
				{summary && <div className='txt-compact-small truncate text-ui-fg-subtle'>{summary}</div>}
				{override.secretsSet.length > 0 && (
					<div className='mt-1 flex flex-wrap gap-1'>
						{override.secretsSet.map(key => (
							<Badge key={key} size='2xsmall' color='green'>
								{fieldLabel(L, key)}
							</Badge>
						))}
					</div>
				)}
			</div>
			{isAdmin && (
				<div className='flex shrink-0 items-center gap-x-2'>
					<Button size='small' variant='secondary' onClick={onEdit}>
						{C.Edit()}
					</Button>
					<Button size='small' variant='transparent' onClick={() => void handleDelete()}>
						<Trash />
					</Button>
				</div>
			)}
		</li>
	)
}

function AddOverrideDrawer({
	config,
	overridableFields,
	onClose
}: {
	config: IntegrationConfig
	overridableFields: IntegrationFieldDescriptor[]
	onClose: () => void
}) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const create = useCreateIntegrationOverride()
	const [saving, setSaving] = useState(false)
	const [targetUuid, setTargetUuid] = useState<string>('')
	const [values, setValues] = useState<FieldValues>(() => initialValues(overridableFields, {}))

	const targets = useTargets(config.overrideScope)

	// A required overridable field (e.g. the Rapyd deviceToken, which only appears once the terminal
	// pairs) must be present before we submit — the create form drops empty values, so otherwise the
	// backend rejects the override as missing a required field.
	const missingRequired = useMemo(() => missingRequiredFields(overridableFields, values), [overridableFields, values])

	const save = useCallback(async () => {
		if (!targetUuid || missingRequired) return
		setSaving(true)
		const result = await create({
			path: { provider: config.provider },
			body: { targetUuid, values: cleanValues(overridableFields, values, [], true) }
		})
		setSaving(false)
		if (result.isSuccess) {
			toast.success(C.Saved())
			onClose()
		} else {
			toast.error(C.SaveError())
		}
	}, [create, config.provider, targetUuid, overridableFields, values, C, onClose, missingRequired])

	return (
		<Drawer open onOpenChange={open => !open && onClose()}>
			<Drawer.Content>
				<Drawer.Header>
					<Drawer.Title>{C.AddOverride()}</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body className='space-y-4 overflow-y-auto'>
					<div className='space-y-1'>
						<Label size='small'>
							{config.overrideScope === 'salesChannel' ? C.TargetSalesChannel() : C.TargetPointOfSale()}
						</Label>
						<Select value={targetUuid} onValueChange={setTargetUuid}>
							<Select.Trigger>
								<Select.Value placeholder={C.SelectTarget()} />
							</Select.Trigger>
							<Select.Content>
								{targets.map(target => (
									<Select.Item key={target.uuid} value={target.uuid}>
										{target.name}
									</Select.Item>
								))}
							</Select.Content>
						</Select>
					</div>
					{overridableFields.map(field => (
						<FieldInput
							key={field.key}
							field={field}
							value={values[field.key]}
							secretSet={false}
							onChange={next => setValues(prev => ({ ...prev, [field.key]: next }))}
							provider={config.provider}
							teyaStoreId={
								typeof config.global.values?.storeId === 'string'
									? config.global.values.storeId
									: undefined
							}
							apiKeyConfigured={config.global.secretsSet.includes('apiKey')}
							siblingValues={values}
							onPatchField={(key, val) => setValues(prev => ({ ...prev, [key]: val }))}
						/>
					))}
				</Drawer.Body>
				<Drawer.Footer>
					<Drawer.Close asChild>
						<Button size='small' variant='secondary'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<Button
						size='small'
						onClick={() => void save()}
						isLoading={saving}
						disabled={!targetUuid || missingRequired}
					>
						{C.Save()}
					</Button>
				</Drawer.Footer>
			</Drawer.Content>
		</Drawer>
	)
}

function EditOverrideDrawer({
	config,
	override,
	overridableFields,
	onClose
}: {
	config: IntegrationConfig
	override: IntegrationOverride
	overridableFields: IntegrationFieldDescriptor[]
	onClose: () => void
}) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const update = useUpdateIntegrationOverride()
	const [saving, setSaving] = useState(false)
	const [values, setValues] = useState<FieldValues>(() => initialValues(overridableFields, override.values ?? {}))

	const save = useCallback(async () => {
		setSaving(true)
		const result = await update({
			path: { provider: config.provider, featureId: String(override.featureId) },
			body: { values: cleanValues(overridableFields, values, override.secretsSet, true) }
		})
		setSaving(false)
		if (result.isSuccess) {
			toast.success(C.Saved())
			onClose()
		} else {
			toast.error(C.SaveError())
		}
	}, [update, config.provider, override, overridableFields, values, C, onClose])

	return (
		<Drawer open onOpenChange={open => !open && onClose()}>
			<Drawer.Content>
				<Drawer.Header>
					<Drawer.Title>
						{C.EditOverride()} — {override.targetName}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body className='space-y-4 overflow-y-auto'>
					{overridableFields.map(field => (
						<FieldInput
							key={field.key}
							field={field}
							value={values[field.key]}
							secretSet={override.secretsSet.includes(field.key)}
							onChange={next => setValues(prev => ({ ...prev, [field.key]: next }))}
							provider={config.provider}
							teyaStoreId={
								typeof config.global.values?.storeId === 'string'
									? config.global.values.storeId
									: undefined
							}
							apiKeyConfigured={config.global.secretsSet.includes('apiKey')}
							siblingValues={values}
							onPatchField={(key, val) => setValues(prev => ({ ...prev, [key]: val }))}
						/>
					))}
				</Drawer.Body>
				<Drawer.Footer>
					<Drawer.Close asChild>
						<Button size='small' variant='secondary'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<Button size='small' onClick={() => void save()} isLoading={saving}>
						{C.Save()}
					</Button>
				</Drawer.Footer>
			</Drawer.Content>
		</Drawer>
	)
}

function FieldInput({
	field,
	value,
	secretSet,
	onChange,
	provider,
	teyaStoreId,
	apiKeyConfigured,
	siblingValues,
	onPatchField,
	disabled = false
}: {
	field: IntegrationFieldDescriptor
	value: unknown
	secretSet: boolean
	onChange: (next: unknown) => void
	/** Provider slug, used to swap generic inputs for provider-specific pickers (e.g. Teya). */
	provider?: string
	/** The store the terminal picker should list terminals for (Teya's terminalId is store-scoped). */
	teyaStoreId?: string
	/** Whether the global Rapyd API key is saved (pairing needs it stored server-side first). */
	apiKeyConfigured?: boolean
	/** Sibling field values, so a widget can read fields other than its own (e.g. Rapyd serial). */
	siblingValues?: FieldValues
	/** Patch a sibling field, so a widget can write more than one field (e.g. Rapyd pairing). */
	onPatchField?: (key: string, value: unknown) => void
	/** Read-only mode for non-admin viewers: inputs are shown but cannot be edited. */
	disabled?: boolean
}) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const label = fieldLabel(L, field.key)

	// Rapyd's serial number is captured and shown by the pairing widget (rendered for the deviceToken
	// field), so it has no standalone input here.
	if (provider === 'rapyd-cloudlink' && field.key === 'serialNumber') {
		return null
	}

	// Rapyd CloudLink pairs a device token per terminal: replace the hand-typed deviceToken with a
	// pairing widget (register serial -> poll until the terminal binds its token). Non-admins get a
	// static status line instead of the pairing controls.
	if (provider === 'rapyd-cloudlink' && field.key === 'deviceToken') {
		const serialNumber = typeof siblingValues?.serialNumber === 'string' ? siblingValues.serialNumber : ''
		return (
			<div className='space-y-1'>
				<Label size='small'>{label}</Label>
				{disabled ? (
					<p className='txt-compact-small text-ui-fg-subtle'>
						{typeof value === 'string' && value
							? `${maskCredential(value)}${serialNumber ? ` · ${fieldLabel(L, 'serialNumber')}: ${serialNumber}` : ''}`
							: C.SecretUnset()}
					</p>
				) : (
					<RapydPairingField
						value={value}
						onChange={onChange}
						serialNumber={serialNumber}
						onSerialChange={next => onPatchField?.('serialNumber', next)}
						apiKeyConfigured={apiKeyConfigured ?? false}
					/>
				)}
			</div>
		)
	}

	// Teya exposes live store/terminal discovery, so replace the hand-typed IDs with pickers backed
	// by the connected tenant's Teya account. eposInstanceId stays a plain text field.
	if (provider === 'teya' && field.key === 'storeId') {
		return (
			<div className='space-y-1'>
				<Label size='small'>{label}</Label>
				<TeyaStoreSelect value={value} onChange={onChange} disabled={disabled} />
			</div>
		)
	}
	if (provider === 'teya' && field.key === 'terminalId') {
		return (
			<div className='space-y-1'>
				<Label size='small'>{label}</Label>
				<TeyaTerminalSelect storeId={teyaStoreId} value={value} onChange={onChange} disabled={disabled} />
			</div>
		)
	}

	if (field.type === 'boolean') {
		return (
			<div className='flex items-center justify-between'>
				<Label size='small'>{label}</Label>
				<Switch checked={value === true} onCheckedChange={checked => onChange(checked)} disabled={disabled} />
			</div>
		)
	}

	if (field.type === 'select') {
		return (
			<div className='space-y-1'>
				<Label size='small'>{label}</Label>
				<Select value={typeof value === 'string' ? value : ''} onValueChange={onChange} disabled={disabled}>
					<Select.Trigger>
						<Select.Value placeholder={C.SelectTarget()} />
					</Select.Trigger>
					<Select.Content>
						{(field.options ?? []).map(option => (
							<Select.Item key={option} value={option}>
								{option}
							</Select.Item>
						))}
					</Select.Content>
				</Select>
			</div>
		)
	}

	if (field.secret) {
		return (
			<div className='space-y-1'>
				<div className='flex items-center justify-between'>
					<Label size='small'>{label}</Label>
					<Badge size='2xsmall' color={secretSet ? 'green' : 'grey'}>
						{secretSet ? C.SecretSet() : C.SecretUnset()}
					</Badge>
				</div>
				<Input
					type='password'
					autoComplete='new-password'
					value={typeof value === 'string' ? value : ''}
					placeholder={secretSet ? C.SecretPlaceholderSet() : C.SecretPlaceholderUnset()}
					onChange={e => onChange(e.target.value)}
					disabled={disabled}
				/>
			</div>
		)
	}

	return (
		<div className='space-y-1'>
			<Label size='small'>{label}</Label>
			<Input
				type={field.type === 'number' ? 'number' : 'text'}
				value={value === undefined || value === null ? '' : String(value)}
				onChange={e => onChange(field.type === 'number' ? numberOrUndefined(e.target.value) : e.target.value)}
				disabled={disabled}
			/>
		</div>
	)
}

// Live Teya store picker. Any query error (typically a 409 when Teya isn't connected) collapses to
// a "reconnect" hint rather than a raw error state.
function TeyaStoreSelect({
	value,
	onChange,
	disabled = false
}: {
	value: unknown
	onChange: (next: unknown) => void
	disabled?: boolean
}) {
	const { L } = useI18n()
	const T = L.Settings.Integrations.Teya
	// Discovery endpoints are ADMIN-only, so skip the query entirely for read-only viewers.
	const stores = useListTeyaStores({ enabled: !disabled })
	const current = typeof value === 'string' ? value : ''

	if (disabled) return <p className='txt-compact-small text-ui-fg-subtle'>{current || T.NoStores()}</p>
	if (stores.isPending) return <LoadingSpinner />
	if (stores.isError) return <p className='txt-compact-small text-ui-fg-subtle'>{T.ReconnectToConfigure()}</p>

	const options = stores.data?.stores ?? []
	if (options.length === 0) return <p className='txt-compact-small text-ui-fg-subtle'>{T.NoStores()}</p>

	return (
		<Select value={current} onValueChange={onChange}>
			<Select.Trigger>
				<Select.Value placeholder={T.SelectStore()} />
			</Select.Trigger>
			<Select.Content>
				{options.map(store => (
					<Select.Item key={store.id} value={store.id}>
						{store.name ?? store.id}
						{store.address ? ` — ${store.address}` : ''}
					</Select.Item>
				))}
			</Select.Content>
		</Select>
	)
}

// Live Teya terminal picker, scoped to the chosen store. Disabled until a store is selected because
// terminals are listed per store.
function TeyaTerminalSelect({
	storeId,
	value,
	onChange,
	disabled = false
}: {
	storeId?: string
	value: unknown
	onChange: (next: unknown) => void
	disabled?: boolean
}) {
	const { L } = useI18n()
	const T = L.Settings.Integrations.Teya
	// Discovery endpoints are ADMIN-only, so skip the query entirely for read-only viewers.
	const terminals = useListTeyaTerminals({ storeId: storeId ?? '' }, { enabled: !disabled && !!storeId })
	const current = typeof value === 'string' ? value : ''

	if (disabled) return <p className='txt-compact-small text-ui-fg-subtle'>{current || T.NoTerminals()}</p>
	if (!storeId) return <p className='txt-compact-small text-ui-fg-subtle'>{T.SelectStoreFirst()}</p>
	if (terminals.isPending) return <LoadingSpinner />
	if (terminals.isError) return <p className='txt-compact-small text-ui-fg-subtle'>{T.ReconnectToConfigure()}</p>

	const options = terminals.data?.terminals ?? []
	if (options.length === 0) return <p className='txt-compact-small text-ui-fg-subtle'>{T.NoTerminals()}</p>

	return (
		<Select value={current} onValueChange={onChange}>
			<Select.Trigger>
				<Select.Value placeholder={T.SelectTerminal()} />
			</Select.Trigger>
			<Select.Content>
				{options.map(terminal => (
					<Select.Item key={terminal.terminalId} value={terminal.terminalId}>
						{terminal.name ?? terminal.terminalId}
						{terminal.serialNumber ? ` — ${terminal.serialNumber}` : ''}
					</Select.Item>
				))}
			</Select.Content>
		</Select>
	)
}

// Rapyd CloudLink device pairing. The merchant enters the PAX serial number and registers it; we
// then poll until the terminal binds its device token to that serial and store the token via the
// normal override save path. Needs the global API key to be saved first (the backend uses it).
function RapydPairingField({
	value,
	onChange,
	serialNumber: pairedSerial,
	onSerialChange,
	apiKeyConfigured
}: {
	value: unknown
	onChange: (next: unknown) => void
	/** The serial already stored for this override, used to seed the input and label the paired device. */
	serialNumber: string
	/** Persist the serial alongside the device token when the terminal pairs. */
	onSerialChange: (next: string) => void
	apiKeyConfigured: boolean
}) {
	const { L } = useI18n()
	const R = L.Settings.Integrations.Rapyd
	const register = useRegisterRapydDevice()
	const [serialNumber, setSerialNumber] = useState(pairedSerial)
	const [pairing, setPairing] = useState(false)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const current = typeof value === 'string' ? value : ''
	const trimmedSerial = serialNumber.trim()

	const poll = usePollRapydDeviceToken(
		{ serialNumber: trimmedSerial },
		{ enabled: pairing && trimmedSerial.length >= RAPYD_SERIAL_MIN_LENGTH, refetchInterval: RAPYD_POLL_INTERVAL_MS }
	)

	const stopPairing = useCallback(() => {
		setPairing(false)
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}, [])

	// Once the terminal pairs, the poll returns the device token; store it (with the serial that was
	// paired) and end the loop.
	useEffect(() => {
		if (pairing && poll.data?.paired && poll.data.deviceToken) {
			onChange(poll.data.deviceToken)
			onSerialChange(trimmedSerial)
			stopPairing()
			toast.success(R.Paired())
		}
	}, [pairing, poll.data, onChange, onSerialChange, trimmedSerial, stopPairing, R])

	// Clear the pending timeout if the user navigates away mid-pairing.
	useEffect(() => stopPairing, [stopPairing])

	const handleRegister = useCallback(async () => {
		if (trimmedSerial.length < RAPYD_SERIAL_MIN_LENGTH) {
			toast.error(R.SerialTooShort())
			return
		}
		const result = await register({ body: { serialNumber: trimmedSerial } })
		if (!result.isSuccess) {
			toast.error(R.RegisterError())
			return
		}
		setPairing(true)
		if (timeoutRef.current) clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => setPairing(false), RAPYD_PAIR_TIMEOUT_MS)
	}, [trimmedSerial, register, R])

	if (!apiKeyConfigured) {
		return <p className='txt-compact-small text-ui-fg-subtle'>{R.ApiKeyRequiredFirst()}</p>
	}

	return (
		<div className='space-y-2'>
			{current && (
				<div className='flex items-center gap-x-2'>
					<Badge size='2xsmall' color='green'>
						{R.Paired()}
					</Badge>
					<span className='txt-compact-small truncate text-ui-fg-subtle'>
						{maskCredential(current)}
						{pairedSerial ? ` · ${fieldLabel(L, 'serialNumber')}: ${pairedSerial}` : ''}
					</span>
				</div>
			)}
			<div className='flex items-end gap-x-2'>
				<div className='flex-1 space-y-1'>
					<Input
						value={serialNumber}
						placeholder={R.SerialPlaceholder()}
						onChange={e => setSerialNumber(e.target.value)}
						disabled={pairing}
					/>
				</div>
				<Button
					size='small'
					variant='secondary'
					onClick={() => void handleRegister()}
					isLoading={pairing}
					disabled={pairing}
				>
					{current ? R.Repair() : R.Register()}
				</Button>
			</div>
			{pairing && <p className='txt-compact-small text-ui-fg-subtle'>{R.WaitingForPairing()}</p>}
		</div>
	)
}

function useTargets(scope: IntegrationConfig['overrideScope']): { uuid: string; name: string }[] {
	const salesChannels = useListSalesChannels({ enabled: scope === 'salesChannel' })
	const pointsOfSale = useListPointsOfSale({ enabled: scope === 'pointOfSale' })
	return useMemo(() => {
		const source = scope === 'salesChannel' ? salesChannels.data : pointsOfSale.data
		return (source ?? []).map(item => ({ uuid: item.uuid, name: item.name }))
	}, [scope, salesChannels.data, pointsOfSale.data])
}

const initialValues = (fields: IntegrationFieldDescriptor[], source: FieldValues): FieldValues => {
	const result: FieldValues = {}
	for (const field of fields) {
		if (field.secret) {
			result[field.key] = ''
		} else if (field.type === 'boolean') {
			result[field.key] = source[field.key] === true
		} else {
			result[field.key] = source[field.key] ?? ''
		}
	}
	return result
}

// Drop empty non-secret values and empty (=unchanged) secrets so the backend can leave them alone.
const cleanValues = (
	fields: IntegrationFieldDescriptor[],
	values: FieldValues,
	_secretsSet: string[],
	overrideOnly: boolean
): FieldValues => {
	const result: FieldValues = {}
	for (const field of fields) {
		if (overrideOnly && !field.overridable) continue
		const value = values[field.key]
		if (field.secret) {
			// A blank (or whitespace-only) secret means "leave unchanged"; sending it would overwrite a
			// stored credential with whitespace. Nonblank values are forwarded verbatim.
			if (typeof value === 'string' && value.trim().length > 0) result[field.key] = value
			continue
		}
		if (field.type === 'boolean') {
			result[field.key] = value === true
			continue
		}
		if (value === undefined || value === null || value === '') continue
		result[field.key] = value
	}
	return result
}

// A required field counts as satisfied once it holds a usable value. Booleans are always set; for
// everything else (including the paired Rapyd deviceToken and typed secrets) we require a non-empty
// value. Mirrors the backend's create-time required check so we can block Save before it 400s.
const missingRequiredFields = (fields: IntegrationFieldDescriptor[], values: FieldValues): boolean =>
	fields.some(field => {
		if (!field.required) return false
		if (field.type === 'boolean') return false
		const value = values[field.key]
		if (typeof value === 'string') return value.trim().length === 0
		return value === undefined || value === null
	})

// Global-row equivalent of the backend's `assertRequiredGlobalFields`: only non-overridable required
// fields must resolve on the global row (overridable ones resolve per scope). A required secret is
// satisfied when a new value is typed OR a value is already stored server-side (`secretsSet`).
const missingRequiredGlobalFields = (
	fields: IntegrationFieldDescriptor[],
	values: FieldValues,
	secretsSet: string[]
): boolean =>
	fields.some(field => {
		if (!field.required || field.overridable) return false
		if (field.type === 'boolean') return false
		if (field.secret) {
			const value = values[field.key]
			const typed = typeof value === 'string' && value.trim().length > 0
			return !typed && !secretsSet.includes(field.key)
		}
		const value = values[field.key]
		if (typeof value === 'string') return value.trim().length === 0
		return value === undefined || value === null
	})

const numberOrUndefined = (raw: string): number | undefined => {
	if (raw.trim() === '') return undefined
	const parsed = Number(raw)
	return Number.isNaN(parsed) ? undefined : parsed
}
